import Component from 'flarum/common/Component';
import { tt } from '../i18n';
import { VIEWBOX, PROVINCES, project, inFrame } from '../data/belgium';

/**
 * Donkere kaart van België met een punt per club.
 *
 * attrs:
 *  - clubs: Club[]           clubs om te tonen (alleen die met coördinaten binnen het kader)
 *  - activeSlug: string|null  geselecteerde club (wordt uitgelicht)
 *  - province: string|null    geselecteerde provincie (wordt uitgelicht)
 *  - onselect(club)           klik op een punt
 *  - compact: bool            kleine variant (clubpagina), zonder labels bij hover
 *  - places: object[]         i.p.v. clubs: plaatsen van één club ({lat, lng, city, …}); bij meer
 *                             dan één genummerd in chronologische volgorde
 */
export default class BelgiumMap extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.hover = null;
  }

  view() {
    if (this.attrs.places) return this.placesView();

    const { clubs = [], activeSlug, province, compact } = this.attrs;
    const pins = this.layout(clubs.filter((c) => c.lat() && c.lng() && inFrame(c.lat(), c.lng())));
    const maxPosts = Math.max(1, ...pins.map((p) => p.club.postCount() || 0));

    return (
      <div className={'LcMap' + (compact ? ' LcMap--compact' : '')} data-province={province || ''}>
        <svg className="LcMap-base" viewBox={VIEWBOX} aria-hidden="true">
          {m.trust(PROVINCES)}
        </svg>
        <svg className="LcMap-pins" viewBox={VIEWBOX} role="img" aria-label={tt('home.map_label')}>
          {pins.map(({ club, x, y }) => {
            const r = compact ? 9 : 5 + 9 * Math.sqrt((club.postCount() || 0) / maxPosts);
            const active = club.slug() === activeSlug;
            return (
              <g
                className={'LcMap-pin' + (active ? ' is-active' : '') + (club.isFeatured() ? ' is-featured' : '')}
                transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}
                tabindex={compact ? undefined : 0}
                role={compact ? undefined : 'link'}
                aria-label={compact ? undefined : `${club.name()}, ${club.city() || ''}`}
                onmouseenter={() => (this.hover = club)}
                onmouseleave={() => (this.hover = null)}
                onfocus={() => (this.hover = club)}
                onblur={() => (this.hover = null)}
                onclick={() => this.attrs.onselect && this.attrs.onselect(club)}
                onkeydown={(e) => e.key === 'Enter' && this.attrs.onselect && this.attrs.onselect(club)}
              >
                {/* Onzichtbaar raakvlak: een punt van enkele pixels is met een vinger niet te
                    raken. De kaart is 1000 eenheden breed, dus 30 eenheden is ~9px op een
                    telefoon en ~14px op desktop. */}
                <circle className="LcMap-hit" r={Math.max(r * 2.2, 30)} />
                <circle className="LcMap-glow" r={r * 2.2} />
                <circle className="LcMap-dot" r={r} />
              </g>
            );
          })}
        </svg>
        {!compact && this.hover ? this.tooltip(this.hover) : null}
      </div>
    );
  }

  placesView() {
    const places = this.attrs.places.filter((p) => p.lat && p.lng && inFrame(p.lat, p.lng));
    const numbered = places.length > 1;
    const pins = this.spread(places.map((p, i) => ({ item: { ...p, n: i + 1 }, xy: project(p.lat, p.lng) })));

    return (
      <div className="LcMap LcMap--compact LcMap--places">
        <svg className="LcMap-base" viewBox={VIEWBOX} aria-hidden="true">
          {m.trust(PROVINCES)}
        </svg>
        <svg className="LcMap-pins" viewBox={VIEWBOX} role="img" aria-label={places.map((p) => p.city).filter(Boolean).join(", ")}>
          {pins.map(({ item, x, y }) => (
            <g className={"LcMap-pin" + (numbered ? " LcMap-pin--numbered" : " is-active")} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}>
              <circle className="LcMap-glow" r={numbered ? 40 : 34} />
              <circle className="LcMap-dot" r={numbered ? 24 : 15} />
              {numbered ? <text className="LcMap-num" dy=".35em">{item.n}</text> : null}
            </g>
          ))}
        </svg>
      </div>
    );
  }

  /** Schuift punten die (bijna) samenvallen uit elkaar, zodat elk nummer leesbaar blijft. */
  spread(points) {
    const out = [];
    points.forEach(({ item, xy: [x0, y0] }) => {
      let x = x0;
      let y = y0;
      // Spiraal rond het echte punt tot er geen ander nummer meer in de weg zit.
      for (let i = 1; i < 60 && out.some((o) => Math.hypot(o.x - x, o.y - y) < 54); i++) {
        const angle = i * 2.4;
        const dist = 10 * Math.sqrt(i) + 14;
        x = x0 + Math.cos(angle) * dist;
        y = y0 + Math.sin(angle) * dist;
      }
      out.push({ item, x, y });
    });
    return out;
  }

  tooltip(club) {
    const [x, y] = project(club.lat(), club.lng());
    const [, , w, h] = VIEWBOX.split(' ').map(Number);
    const pct = (x / w) * 100;
    // De tooltip staat normaal gecentreerd boven het punt. Bij een punt aan de rand van de
    // kaart zou de helft buiten het scherm vallen; dan klapt hij vanaf het punt naar binnen.
    const edge = pct < 24 ? ' LcMap-tooltip--start' : pct > 76 ? ' LcMap-tooltip--end' : '';
    return (
      <div className={'LcMap-tooltip' + edge} style={{ left: `${pct}%`, top: `${(y / h) * 100}%` }}>
        <strong>{club.name()}</strong>
        <span>{[club.city(), club.years()].filter(Boolean).join(' · ')}</span>
      </div>
    );
  }

  /**
   * Zet clubs om naar kaartposities. Clubs op (bijna) dezelfde plek worden in een kleine spiraal
   * rond het middelpunt geschoven, zodat ze allemaal aanklikbaar blijven.
   */
  layout(clubs) {
    const groups = new Map();
    clubs.forEach((club) => {
      const [x, y] = project(club.lat(), club.lng());
      const key = `${Math.round(x / 8)}:${Math.round(y / 8)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ club, x, y });
    });

    const out = [];
    groups.forEach((items) => {
      items.forEach((item, i) => {
        if (i === 0) return out.push(item);
        const angle = i * 2.4; // gulden hoek
        const dist = 7 + 4 * Math.sqrt(i);
        out.push({ club: item.club, x: item.x + Math.cos(angle) * dist, y: item.y + Math.sin(angle) * dist });
      });
    });
    return out;
  }
}
